import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Home, TrendingUp, ArrowDown, Building2, FileCheck, Palette, 
  CheckCircle, ArrowRight, Phone, Mail, MapPin, X, Calendar, Loader2
} from 'lucide-react';
import { architecturalServices, architectureProjects, architectureProcess, architectureStats } from '../architectureMockData';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Architecture = () => {
  const { toast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [consultationForm, setConsultationForm] = useState({
    project_type: '',
    name: '',
    email: '',
    phone: '',
    property_address: '',
    preferred_date: '',
    preferred_time: '',
    budget_range: '',
    message: ''
  });

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    if (!consultationForm.name || !consultationForm.phone || !consultationForm.project_type || !consultationForm.preferred_date) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/consultation-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(consultationForm)
      });

      if (response.ok) {
        toast({
          title: "Consultation Request Submitted!",
          description: "We'll contact you within 24 hours to confirm your appointment."
        });
        setShowConsultationForm(false);
        setConsultationForm({
          project_type: '', name: '', email: '', phone: '',
          property_address: '', preferred_date: '', preferred_time: '',
          budget_range: '', message: ''
        });
      } else {
        const error = await response.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Please try again", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const iconMap = {
    Home: Home,
    TrendingUp: TrendingUp,
    ArrowDown: ArrowDown,
    Building2: Building2,
    FileCheck: FileCheck,
    Palette: Palette
  };

  const filteredProjects = selectedCategory === 'all' 
    ? architectureProjects 
    : architectureProjects.filter(p => p.type.toLowerCase().includes(selectedCategory.toLowerCase()));

  return (
    <div className="min-h-screen pt-20 pb-16 bg-white">
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center bg-gradient-to-br from-[#404040] to-[#2a2a2a]">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white z-10">
          <div className="max-w-3xl fade-in">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Architectural Services in London
            </h1>
            <p className="text-xl mb-8 text-gray-200">
              Transform your property with award-winning architectural design and planning expertise
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/architecture/quote">
                <Button size="lg" className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white text-lg px-8 py-6 btn-hover-lift">
                  Get Design Quote
                </Button>
              </Link>
              <Link to="/architecture/build-cost">
                <Button size="lg" variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#404040] text-lg px-8 py-6">
                  Get Build Cost Estimate
                </Button>
              </Link>
              <Link to="/client/login">
                <Button size="lg" variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#404040] text-lg px-8 py-6">
                  Client Portal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <Link to="/architecture/quote">
              <Card className="card-hover border-none shadow-lg hover:shadow-xl transition-all cursor-pointer h-full">
                <CardContent className="p-8 flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-[#404040] mb-2">Get Design Cost Estimate</h3>
                    <p className="text-gray-600 mb-4">Calculate your architectural design costs in 30 seconds</p>
                    <div className="text-[#FF8C42] font-semibold flex items-center">
                      Start Now <ArrowRight className="w-5 h-5 ml-2" />
                    </div>
                  </div>
                  <div className="text-6xl">📐</div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/architecture/build-cost">
              <Card className="card-hover border-none shadow-lg hover:shadow-xl transition-all cursor-pointer h-full">
                <CardContent className="p-8 flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-[#404040] mb-2">Get Build Cost Estimate</h3>
                    <p className="text-gray-600 mb-4">Estimate your construction costs with quality options</p>
                    <div className="text-[#FF8C42] font-semibold flex items-center">
                      Calculate Now <ArrowRight className="w-5 h-5 ml-2" />
                    </div>
                  </div>
                  <div className="text-6xl">🏗️</div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/architecture/enquiry">
              <Card className="card-hover border-none shadow-lg hover:shadow-xl transition-all cursor-pointer h-full bg-gradient-to-br from-[#404040] to-[#2a2a2a] text-white">
                <CardContent className="p-8 flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Project Enquiry</h3>
                    <p className="opacity-80 mb-4">Speak to our architectural experts about your project</p>
                    <div className="text-[#FF8C42] font-semibold flex items-center">
                      Start Enquiry <ArrowRight className="w-5 h-5 ml-2" />
                    </div>
                  </div>
                  <div className="text-6xl">💬</div>
                </CardContent>
              </Card>
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {architectureStats.map((stat, index) => (
              <div key={index} className="text-center fade-in">
                <div className="stat-number mb-2">{stat.value}</div>
                <div className="text-gray-600 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#404040] mb-4">
              Our Architectural Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From extensions to new builds, we provide comprehensive architectural services across London
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {architecturalServices.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="card-hover border-none shadow-lg overflow-hidden">
                  <div className="relative h-48">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="bg-[#FF8C42] p-2 rounded-lg inline-block">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-[#404040] mb-3">{service.name}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="space-y-2 mb-4">
                      {service.types.slice(0, 3).map((type, idx) => (
                        <div key={idx} className="flex items-center text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-[#FF8C42] mr-2 flex-shrink-0" />
                          {type}
                        </div>
                      ))}
                      {service.types.length > 3 && (
                        <p className="text-sm text-gray-500 italic">+{service.types.length - 3} more</p>
                      )}
                    </div>
                    <Link to="/contact">
                      <Button className="w-full bg-[#404040] hover:bg-[#FF8C42] text-white">
                        Learn More
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#404040] mb-4">
              Our Design Process
            </h2>
            <p className="text-lg text-gray-600">
              A streamlined approach from concept to completion
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {architectureProcess.map((item, index) => (
              <Card key={index} className="border-none shadow-lg p-6 relative">
                <div className="flex items-start mb-4">
                  <div className="bg-[#FF8C42] text-white font-bold text-xl w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                    {item.step}
                  </div>
                  {index < architectureProcess.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-16 w-full h-0.5 bg-gray-200" style={{ width: 'calc(100% + 2rem)' }}></div>
                  )}
                </div>
                <h3 className="text-xl font-bold text-[#404040] mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#404040] mb-4">
              Recent Projects
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Explore our portfolio of completed architectural projects across London
            </p>

            {/* Filter Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                onClick={() => setSelectedCategory('all')}
                className={selectedCategory === 'all' ? 'bg-[#FF8C42] hover:bg-[#FF6B35] text-white' : 'border-[#FF8C42] text-[#FF8C42] hover:bg-[#FF8C42] hover:text-white'}
              >
                All Projects
              </Button>
              <Button
                variant={selectedCategory === 'extension' ? 'default' : 'outline'}
                onClick={() => setSelectedCategory('extension')}
                className={selectedCategory === 'extension' ? 'bg-[#FF8C42] hover:bg-[#FF6B35] text-white' : 'border-[#FF8C42] text-[#FF8C42] hover:bg-[#FF8C42] hover:text-white'}
              >
                Extensions
              </Button>
              <Button
                variant={selectedCategory === 'loft' ? 'default' : 'outline'}
                onClick={() => setSelectedCategory('loft')}
                className={selectedCategory === 'loft' ? 'bg-[#FF8C42] hover:bg-[#FF6B35] text-white' : 'border-[#FF8C42] text-[#FF8C42] hover:bg-[#FF8C42] hover:text-white'}
              >
                Loft Conversions
              </Button>
              <Button
                variant={selectedCategory === 'basement' ? 'default' : 'outline'}
                onClick={() => setSelectedCategory('basement')}
                className={selectedCategory === 'basement' ? 'bg-[#FF8C42] hover:bg-[#FF6B35] text-white' : 'border-[#FF8C42] text-[#FF8C42] hover:bg-[#FF8C42] hover:text-white'}
              >
                Basements
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="property-card-hover overflow-hidden border-none shadow-lg">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="property-image w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-[#FF8C42] text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {project.type}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#404040] mb-2">{project.title}</h3>
                  <div className="flex items-center text-gray-600 mb-2">
                    <MapPin className="w-4 h-4 mr-1 text-[#FF8C42]" />
                    <span className="text-sm">{project.location}</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{project.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">{project.year}</span>
                    <Link to="/architecture/quote">
                      <Button variant="outline" size="sm" className="border-[#FF8C42] text-[#FF8C42] hover:bg-[#FF8C42] hover:text-white">
                        Start Your Project
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#404040] mb-4">
              Why Choose Harry & Jacobs Architecture?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-none shadow-lg p-6 text-center card-hover">
              <div className="bg-[#FF8C42] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#404040] mb-3">Expert Team</h3>
              <p className="text-gray-600">
                ARB registered architects with 15+ years of London experience
              </p>
            </Card>

            <Card className="border-none shadow-lg p-6 text-center card-hover">
              <div className="bg-[#FF8C42] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileCheck className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#404040] mb-3">Planning Success</h3>
              <p className="text-gray-600">
                95% planning approval rate across all London boroughs
              </p>
            </Card>

            <Card className="border-none shadow-lg p-6 text-center card-hover">
              <div className="bg-[#FF8C42] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#404040] mb-3">Full Support</h3>
              <p className="text-gray-600">
                From initial concept through to completion and beyond
              </p>
            </Card>

            <Card className="border-none shadow-lg p-6 text-center card-hover">
              <div className="bg-[#FF8C42] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#404040] mb-3">Local Knowledge</h3>
              <p className="text-gray-600">
                Deep understanding of London property and planning regulations
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#404040] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Property?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Get an instant quote for your architectural project in just 5 steps
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/architecture/quote">
              <Button size="lg" className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white text-lg px-8 py-6 btn-hover-lift">
                Get Instant Quote
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#404040] text-lg px-8 py-6"
              onClick={() => setShowConsultationForm(true)}
              data-testid="book-consultation-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book Free Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Consultation Form Modal */}
      {showConsultationForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <Card className="w-full max-w-xl my-8">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold text-[#404040]">Book a Free Consultation</h3>
                  <p className="text-sm text-gray-500">Speak with our expert architects</p>
                </div>
                <button 
                  onClick={() => setShowConsultationForm(false)} 
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleConsultationSubmit} className="space-y-4">
                <div>
                  <Label>Project Type *</Label>
                  <select
                    value={consultationForm.project_type}
                    onChange={(e) => setConsultationForm({...consultationForm, project_type: e.target.value})}
                    className="w-full p-3 border rounded-lg mt-1"
                    required
                    data-testid="project-type-select"
                  >
                    <option value="">Select project type...</option>
                    <option value="extension">House Extension</option>
                    <option value="loft">Loft Conversion</option>
                    <option value="basement">Basement Conversion</option>
                    <option value="new_build">New Build</option>
                    <option value="renovation">Renovation</option>
                    <option value="interior_design">Interior Design</option>
                    <option value="planning">Planning Application</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={consultationForm.name}
                      onChange={(e) => setConsultationForm({...consultationForm, name: e.target.value})}
                      required
                      data-testid="consultation-name"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      value={consultationForm.phone}
                      onChange={(e) => setConsultationForm({...consultationForm, phone: e.target.value})}
                      required
                      data-testid="consultation-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={consultationForm.email}
                    onChange={(e) => setConsultationForm({...consultationForm, email: e.target.value})}
                    data-testid="consultation-email"
                  />
                </div>

                <div>
                  <Label>Property Address</Label>
                  <Input
                    value={consultationForm.property_address}
                    onChange={(e) => setConsultationForm({...consultationForm, property_address: e.target.value})}
                    placeholder="Where is the project located?"
                    data-testid="consultation-address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Preferred Date *</Label>
                    <Input
                      type="date"
                      value={consultationForm.preferred_date}
                      onChange={(e) => setConsultationForm({...consultationForm, preferred_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      data-testid="consultation-date"
                    />
                  </div>
                  <div>
                    <Label>Preferred Time</Label>
                    <select
                      value={consultationForm.preferred_time}
                      onChange={(e) => setConsultationForm({...consultationForm, preferred_time: e.target.value})}
                      className="w-full p-3 border rounded-lg mt-1"
                      data-testid="consultation-time"
                    >
                      <option value="">Any time</option>
                      <option value="09:00">9:00 AM</option>
                      <option value="10:00">10:00 AM</option>
                      <option value="11:00">11:00 AM</option>
                      <option value="12:00">12:00 PM</option>
                      <option value="14:00">2:00 PM</option>
                      <option value="15:00">3:00 PM</option>
                      <option value="16:00">4:00 PM</option>
                      <option value="17:00">5:00 PM</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label>Budget Range</Label>
                  <select
                    value={consultationForm.budget_range}
                    onChange={(e) => setConsultationForm({...consultationForm, budget_range: e.target.value})}
                    className="w-full p-3 border rounded-lg mt-1"
                    data-testid="consultation-budget"
                  >
                    <option value="">Select budget range...</option>
                    <option value="under_50k">Under £50,000</option>
                    <option value="50k_100k">£50,000 - £100,000</option>
                    <option value="100k_200k">£100,000 - £200,000</option>
                    <option value="200k_500k">£200,000 - £500,000</option>
                    <option value="over_500k">Over £500,000</option>
                    <option value="unsure">Not sure yet</option>
                  </select>
                </div>

                <div>
                  <Label>Tell us about your project</Label>
                  <Textarea
                    value={consultationForm.message}
                    onChange={(e) => setConsultationForm({...consultationForm, message: e.target.value})}
                    rows={3}
                    placeholder="What would you like to achieve?"
                    data-testid="consultation-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-[#FF8C42] hover:bg-[#FF6B35] text-white py-6"
                  data-testid="submit-consultation-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Calendar className="w-5 h-5 mr-2" />
                      Book Consultation
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting, you agree to be contacted regarding your enquiry.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
